import clsx from 'clsx';
// Import directo al fichero (no al barrel `ui`) para romper el ciclo de require:
// barrel `ui` → OptionsDropdownMenu → este fichero → barrel `ui`.
import Hr from 'ui/atoms/Hr/Hr';
import Icon from 'ui/atoms/Icon/Icon';
import TooltipCustom from 'ui/atoms/Tooltip/Tooltip';
import { uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';

const ListSimpleDropdown = ({ items, onClick, colorIcon, colorIconActive, sizeIcon, preventEvent }) => {
  const listRef = useRef(null);

  function getColor(item) {
    let color = item.colorIcon;
    if (item.color) {
      color = item.color;
    } else if (!color && colorIconActive && item.active) {
      color = colorIconActive;
    } else if (!color) {
      color = colorIcon;
    }

    return !item.disabled ? color : 'grey-04';
  }

  function getOnClick(item, event) {
    if (preventEvent) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (item.disabled) {
      return () => null;
    }

    // Try component onClick first
    if (onClick) {
      return onClick(item, event);
    }

    // Then try item onClick with type check
    if (item.onClick && typeof item.onClick === 'function') {
      return item.onClick(item, event);
    }

    // No-op for items without handlers (prevents crash)
    return null;
  }

  function _renderItem(item) {
    return (
      <div className="list-simple-dropdown__texts">
        <span className={clsx('list-simple-dropdown__name', { disabled: item.disabled, 'list-simple-dropdown__title': item.id === 'title' })}>
          {item.name}{' '}
        </span>
        <span className="list-simple-dropdown__description">{item.description} </span>
      </div>
    );
  }

  const renderList = (listItems) => (
    <ul>
      {listItems.map((item) =>
        item.id === 'separator' ? (
          <li className="list-simple-dropdown__separator" aria-hidden="true" key={uniqueId()}>
            <Hr size="small" />
          </li>
        ) : (
          <li
            ref={listRef}
            id={item.id}
            className={clsx(
              'list-simple-dropdown__li',
              {
                'list-simple-dropdown__li--active': item.active,
                'list-simple-dropdown__li--disabled': item.disabled,
                'list-simple-dropdown__li--title': item.id === 'title',
              },
              item.className
            )}
            key={item.name}
            onClick={(event) => getOnClick(item, event)}
          >
            {item.icon && (
              <div className="list-simple-dropdown__icon">
                <Icon name={item.icon} color={getColor(item)} size={sizeIcon} />
              </div>
            )}

            {item.tooltip ? (
              <TooltipCustom title={item.tooltip} placement="right" hasArrow>
                {_renderItem(item)}
              </TooltipCustom>
            ) : (
              _renderItem(item)
            )}

            {item.rightIcon && (
              <div className="list-simple-dropdown__icon list-simple-dropdown__icon--right">
                <Icon name={item.rightIcon} color={colorIconActive && item.active ? colorIconActive : colorIcon} size={sizeIcon} />
              </div>
            )}
          </li>
        )
      )}
    </ul>
  );

  return (
    <div className="list-simple-dropdown">
      {Array.isArray(items[0]) ? items.map((listItems, index) => <div key={index}>{renderList(listItems)}</div>) : renderList(items)}
    </div>
  );
};

ListSimpleDropdown.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        active: PropTypes.bool,
        description: PropTypes.string,
        disabled: PropTypes.bool,
        icon: PropTypes.string,
        id: PropTypes.string,
        key: PropTypes.string,
        name: PropTypes.any,
        onClick: PropTypes.func,
        rightIcon: PropTypes.string,
        tooltip: PropTypes.string,
      }),
      PropTypes.arrayOf(
        PropTypes.shape({
          active: PropTypes.bool,
          description: PropTypes.string,
          disabled: PropTypes.bool,
          icon: PropTypes.string,
          id: PropTypes.string,
          key: PropTypes.string,
          name: PropTypes.any,
          onClick: PropTypes.func,
          rightIcon: PropTypes.string,
          tooltip: PropTypes.string,
        })
      ),
    ])
  ),
  onClick: PropTypes.func,
  colorIcon: PropTypes.oneOf(['', 'default', 'primary', 'label']),
  colorIconActive: PropTypes.oneOf(['', 'default', 'primary', 'label']),
  sizeIcon: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl', '3xl']),
  preventEvent: PropTypes.bool,
};

ListSimpleDropdown.defaultProps = {
  items: [],
  onClick: undefined,
  colorIcon: 'default',
  sizeIcon: 'md',
  preventEvent: false,
};

export default ListSimpleDropdown;
